import { useTranscriptionsState } from '@modules/transcriptions/useTranscriptionsState';
import { ListItemText } from '@mui/material';
import { Stack } from '@mui/system';
import { MouseEvent, useMemo } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { StyledChannelContainer } from 'src/components/StyledChannelContainer/StyledChannelContainer';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { NavActionButton } from 'src/features/Navbar/NavActionButton/NavActionButton';
import { NavItem } from 'src/features/Navbar/NavItem';
import { VoiceChannelParticipants } from 'src/features/VoiceChannelParticipants';
import { ChannelAvatar } from 'src/features/channel/ChannelAvatar';
import { Roles } from 'src/features/workspace/WorkspaceShareMemberRoleSelectProps';
import useActiveLink from 'src/hooks/useActiveLink';
import useCopyToClipboard from 'src/hooks/useCopyToClipboard';
import { useLocales } from 'src/locales';
import {
  useVoiceChannelContext,
  useVoiceChannelsMembers,
} from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { useVoiceChannelInviteModal } from 'src/sections/@dashboard/chat/ChatVoiceChannel/VoiceChannelInvite/VoiceChannelInviteModal';
import {
  ChatDetailsTitle,
  useChatDetailsDialog,
} from 'src/sections/@dashboard/chat/shared/ChatOverviewDialog';
import { getExternalGatheringUrl } from '../ChatVoiceChannel/VoiceChannelInvite/VoiceChannelInviteModal/utils';
import { useChatAllowedAction } from '../shared/useChatAllowedAction';

type Props = {
  conversation: IChatConversation;
};

export const ChatNavItemVoice = ({ conversation }: Props) => {
  const { copy } = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();

  const { connectVoiceChannel } = useVoiceChannelContext();
  const { voiceMembers, isChannelActive } = useVoiceChannelsMembers(
    conversation,
    true,
  );

  const { activeTranscriptionChannelId } = useTranscriptionsState();

  const userPermissions = useChatAllowedAction({ conversation });

  const path = PATH_DASHBOARD.chat.view(conversation._id);
  const { active } = useActiveLink(path, false);

  const { showChatDetailsDialog } = useChatDetailsDialog();
  const { inviteUsers } = useVoiceChannelInviteModal();
  const { activeVoiceChannel, connectionStatus, updateLocalParticipant } =
    useLivekitRTCState();

  const isConnected = useMemo(
    () =>
      activeVoiceChannel?.channel?._id === conversation._id &&
      connectionStatus !== 'disconnected',
    [connectionStatus, activeVoiceChannel?.channel?._id, conversation._id],
  );

  const handleShowDetails = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    showChatDetailsDialog({
      conversationId: conversation._id,
      tab: ChatDetailsTitle.OVERVIEW,
    });
  };

  const handleInviteUsers = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    inviteUsers(e);
  };

  const handleConnectVoiceChannel = (channel: IChatConversation) => {
    connectVoiceChannel({ channel });
    updateLocalParticipant({ role: userPermissions.role || Roles.MEMBER });
  };

  const handleCopyLink = async (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!conversation.externalLink) return;
    await copy(getExternalGatheringUrl(conversation.externalLink));
    enqueueSnackbar(translate('shared.snackbar.invite_url_copied'));
  };

  return (
    <StyledChannelContainer
      connected={isConnected}
      active={active}
      hasConnection={!!voiceMembers.length}
    >
      <NavItem
        depth={1}
        onClick={() => {
          handleConnectVoiceChannel(conversation);
        }}
        path={path}
        selected={isChannelActive}
        connected={isConnected}
        icon={<ChannelAvatar key={conversation._id} channel={conversation} />}
        action={
          <>
            {isConnected && (
              <NavActionButton
                icon="tabler:users-plus"
                onClick={handleInviteUsers}
                size={16}
                color="default"
              />
            )}
            {!!conversation.externalLink && (
              <NavActionButton
                icon="tabler:link"
                onClick={handleCopyLink}
                size={16}
                color="default"
              />
            )}
            <NavActionButton
              icon="tabler:settings"
              onClick={handleShowDetails}
              size={16}
              color="default"
            />
          </>
        }
      >
        <Stack direction="row" alignItems="center" gap={1} overflow="hidden">
          <ListItemText
            primary={conversation.name}
            sx={{
              m: 0,
            }}
            primaryTypographyProps={{
              noWrap: true,
              variant: active ? 'subtitle2' : 'body2',
            }}
          />
          {activeTranscriptionChannelId === conversation._id && (
            <Iconify icon="tabler:file-text-spark" color="text.secondary" />
          )}
        </Stack>
      </NavItem>
      {!!voiceMembers?.length && (
        <VoiceChannelParticipants
          conversationId={conversation._id}
          participants={voiceMembers}
          isConnected={isConnected}
          isAdmin={userPermissions.manageMembers}
          sx={{ pl: 1 }}
        />
      )}
    </StyledChannelContainer>
  );
};
